import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Mail, Phone, MapPin, ShieldCheck, Lock, MapPinned, Facebook, Instagram, Twitter, Youtube } from "lucide-react";
import useSiteSettings from "@/hooks/useSiteSettings";

export default function SiteFooter() {
  const s = useSiteSettings() || {};
  const socials = [
    { url: s.linkedin_url, icon: Linkedin, label: "LinkedIn", testid: "footer-linkedin" },
    { url: s.facebook_url, icon: Facebook, label: "Facebook", testid: "footer-facebook" },
    { url: s.instagram_url, icon: Instagram, label: "Instagram", testid: "footer-instagram" },
    { url: s.twitter_url, icon: Twitter, label: "X", testid: "footer-twitter" },
    { url: s.youtube_url, icon: Youtube, label: "YouTube", testid: "footer-youtube" },
  ].filter(x => x.url);

  return (
    <footer className="lp-footer" data-testid="site-footer">
      <div className="lp-container" style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1fr", gap: 40, padding: "40px 0 24px" }}>
        <div>
          <div className="font-serif-display" style={{ fontSize: 18, fontWeight: 600, marginBottom: 10 }}>
            {s.company_name || "CanadaMRO Exchange"}
          </div>
          <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".05em", color: "var(--ink-mute)", lineHeight: 1.6 }}>
            {s.office_address && <div data-testid="footer-address" style={{ display: "flex", gap: 6, alignItems: "flex-start" }}><MapPin size={11} style={{ marginTop: 3, flexShrink: 0 }} />{s.office_address}</div>}
            {s.support_email && <div data-testid="footer-support-email" style={{ display: "flex", gap: 6, alignItems: "center" }}><Mail size={11} /><a href={`mailto:${s.support_email}`} style={{ color: "inherit" }}>{s.support_email}</a></div>}
            {s.phone && <div data-testid="footer-phone" style={{ display: "flex", gap: 6, alignItems: "center" }}><Phone size={11} />{s.phone}</div>}
          </div>
        </div>

        <div>
          <div className="font-mono" style={{ fontSize: 10, letterSpacing: ".15em", color: "var(--ink-mute)", marginBottom: 12 }}>PRODUCT</div>
          <FooterLink to="/login">Sign in</FooterLink>
          <FooterLink to="/register?role=buyer">Become a buyer</FooterLink>
          <FooterLink to="/register?role=supplier">Become a supplier</FooterLink>
          <FooterLink to="/blog">Blog</FooterLink>
        </div>

        <div>
          <div className="font-mono" style={{ fontSize: 10, letterSpacing: ".15em", color: "var(--ink-mute)", marginBottom: 12 }}>LEGAL</div>
          <FooterLink to="/terms">Terms</FooterLink>
          <FooterLink to="/privacy">Privacy</FooterLink>
          <FooterLink to="/data-handling">Data handling</FooterLink>
          <FooterLink to="/cookies">Cookies</FooterLink>
        </div>

        <div>
          <div className="font-mono" style={{ fontSize: 10, letterSpacing: ".15em", color: "var(--ink-mute)", marginBottom: 12 }}>CONNECT</div>
          {socials.length === 0 && (
            <span style={{ fontSize: 12, color: "var(--ink-mute)" }}>Coming soon.</span>
          )}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {socials.map(({ url, icon: Icon, label, testid }) => (
              <a key={label} href={url} target="_blank" rel="noopener noreferrer"
                 data-testid={testid}
                 aria-label={label}
                 style={{ display: "inline-grid", placeItems: "center", width: 36, height: 36, borderRadius: 6, background: "rgba(255,255,255,.04)", color: "var(--ink-dim)", textDecoration: "none" }}>
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Trust signals row */}
      <div className="lp-container" data-testid="footer-trust-signals" style={{ display: "flex", flexWrap: "wrap", gap: 18, padding: "12px 0", fontSize: 11, color: "var(--ink-mute)", borderTop: "1px solid var(--line)" }}>
        <TrustItem icon={ShieldCheck} label="Verified-only network" />
        <TrustItem icon={Lock} label="TLS 1.2+ encryption · bcrypt password hashing" />
        <TrustItem icon={MapPinned} label="PIPEDA-aware · Hosted in North America" />
      </div>

      <div className="lp-container font-mono" style={{ borderTop: "1px solid var(--line)", padding: "16px 0", fontSize: 11, letterSpacing: ".05em", color: "var(--ink-mute)", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <span data-testid="footer-copyright">{s.copyright || "© CanadaMRO Exchange"}</span>
        <span>BUILT IN ONTARIO · CAD NATIVE</span>
      </div>
    </footer>
  );
}

function FooterLink({ to, children }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <Link to={to} style={{ color: "var(--ink-dim)", fontSize: 13, textDecoration: "none" }}>{children}</Link>
    </div>
  );
}

function TrustItem({ icon: Icon, label }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <Icon size={12} color="var(--amber-400)" /> {label}
    </span>
  );
}
